import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { subDays } from "date-fns";
import Button from "common/pages/morning/-components/common/Button";
import DayPickerInput from "common/pages/morning/-components/common/DayPickerInput";
import Input from "common/pages/morning/-components/common/Input";
import SelectBox from "common/pages/morning/-components/common/SelectBox";
import { useGetConsoleOrderCustomer } from "hooks/console/useGetConsoleOrderCustomer";
import { toastErrorSyle } from "hooks/utils/util";

export type SearchFormProps = {
    category: string;
    user_id: string;
    limit: string;
    from_date: Date | undefined;
    to_date: Date | undefined;
    serial_number: string;
    order_state: string;
    delivery_type: string;
    car_type: string;
    departure_dong_name: string;
    payment_type: string;
    destination_dong_name: string;
};

type ConsoleOrderFormProps = {
    searchForm: SearchFormProps;
    setSearchForm: React.Dispatch<React.SetStateAction<SearchFormProps>>;
    resetForm: () => void;
    searchHandler: () => void;
};
export const ranges = [
    { label: "오늘", value: 0 },
    { label: "어제", value: 1 },
    { label: "3일", value: 2 },
    { label: "7일", value: 6 },
    { label: "10일", value: 9 },
    { label: "20일", value: 19 },
    { label: "30일", value: 29 },
];
export default function ConsoleOrderForm({ searchForm, setSearchForm, resetForm, searchHandler }: ConsoleOrderFormProps) {
    const [selectedRange, setSelectedRange] = useState<number | null>(0);
    const { data: customerData } = useGetConsoleOrderCustomer();

    useEffect(() => {
        const today = new Date();

        const isSameDate = (d1?: Date, d2?: Date) =>
            d1?.getFullYear() === d2?.getFullYear() && d1?.getMonth() === d2?.getMonth() && d1?.getDate() === d2?.getDate();

        for (const range of ranges) {
            const expectedStart = subDays(today, range.value);
            const expectedEnd = today;

            if (isSameDate(searchForm.from_date, expectedStart) && isSameDate(searchForm.to_date, expectedEnd)) {
                setSelectedRange(range.value);
                return;
            }
        }

        // 어떤 range에도 해당하지 않으면 null로
        setSelectedRange(null);
    }, [searchForm.from_date, searchForm.to_date]);

    const handleRangeClick = (days: number) => {
        setSelectedRange(days);
        const today = new Date();
        const from = subDays(today, days);
        setSearchForm(prev => ({ ...prev, from_date: from, to_date: today }));
    };

    // 거래처 select 옵션
    const transformedCustomerData = customerData?.map(item => ({
        label: item.m_name,
        value: item.user_id,
    }));

    useEffect(() => {
        toast.remove();
    }, []);

    const onClickSearch = () => {
        if (searchForm.category === "quick") {
            // 퀵은 거래처 아이디가 필수값임
            if (!searchForm.user_id) {
                toast("거래처를 선택해주세요.", toastErrorSyle);
                return;
            }
        }

        if (!searchForm.from_date || !searchForm.to_date) {
            toast("접수일자를 선택해주세요.", toastErrorSyle);
            return;
        }
        toast.remove();

        searchHandler();
    };

    return (
        <>
            <div className="bg-white flex flex-col items-center gap-4 rounded-[20px] py-[24px] px-[40px]">
                <div className="bg-[#F7F7F8] w-full p-5 flex flex-col gap-3">
                    <div className="flex items-center">
                        <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">접수일자</p>
                        <div className="flex border font-[500] border-new rounded-lg h-[50px] mr-4 overflow-hidden">
                            {ranges.map(({ label, value }, idx) => (
                                <button
                                    key={value}
                                    className={`w-[82px] border-new ${
                                        idx < 6 ? "border-r" : ""
                                    } ${selectedRange === value ? "bg-new text-white" : "bg-white text-[#1b1c1e]"}`}
                                    onClick={() => handleRangeClick(value)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-3 relative bg-white h-[52px] px-3 border border-[#E1E2E4] rounded-lg">
                            <DayPickerInput
                                initialDate={searchForm.from_date}
                                twoCalendar
                                onDateChange={value => setSearchForm(prev => ({ ...prev, from_date: value }))}
                                maxDate={searchForm.to_date}
                            />
                            <DayPickerInput
                                initialDate={searchForm.to_date}
                                twoCalendar
                                onDateChange={value => setSearchForm(prev => ({ ...prev, to_date: value }))}
                                minDate={searchForm.from_date}
                            />
                        </div>
                    </div>
                    <hr />
                    <div className="w-full flex gap-10">
                        <div className="flex w-full items-center">
                            <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">
                                거래처
                                {searchForm.category === "quick" && <span className="text-[#FF7B2E] ml-1">*</span>}
                            </p>
                            <SelectBox
                                options={transformedCustomerData}
                                value={searchForm.user_id}
                                onChange={val => setSearchForm(prev => ({ ...prev, user_id: val }))}
                                className="bg-white w-full"
                            />
                        </div>
                        <div className="flex w-full items-center">
                            <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">처리상태</p>
                            <SelectBox
                                options={[
                                    { label: "대기", value: "대기" },
                                    { label: "완료", value: "완료" },
                                ]}
                                value={searchForm.order_state}
                                onChange={val => setSearchForm(prev => ({ ...prev, order_state: val }))}
                                className="bg-white w-full"
                            />
                        </div>
                        <div className="flex w-full items-center">
                            <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">배송수단</p>
                            <SelectBox
                                options={[
                                    { label: "오토바이", value: "오토바이" },
                                    { label: "다마스", value: "다마스" },
                                    { label: "라보", value: "라보" },
                                    { label: "트럭", value: "트럭" },
                                    { label: "벤", value: "벤" },
                                    searchForm.category === "quick" ? { label: "지하철", value: "지하철" } : null,
                                ].filter((opt): opt is { label: string; value: string } => opt !== null)}
                                value={searchForm.car_type}
                                onChange={val => setSearchForm(prev => ({ ...prev, car_type: val }))}
                                className="bg-white w-full"
                            />
                        </div>
                    </div>
                    <hr />
                    <div className="w-full flex gap-10">
                        <div className="flex w-1/3 items-center">
                            <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">출발지동명</p>
                            <Input
                                value={searchForm.departure_dong_name}
                                onChange={e => setSearchForm(prev => ({ ...prev, departure_dong_name: e.target.value }))}
                                className="w-full"
                            />
                        </div>
                        <div className="flex w-1/3 items-center">
                            <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">배송방법</p>
                            <SelectBox
                                options={[
                                    { label: "편도", value: "편도" },
                                    { label: "왕복", value: "왕복" },
                                    { label: "경유", value: "경유" },
                                ]}
                                value={searchForm.delivery_type}
                                onChange={val => setSearchForm(prev => ({ ...prev, delivery_type: val }))}
                                className="bg-white w-full"
                            />
                        </div>
                        <div className="flex w-1/3 items-center">
                            <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">지불수단</p>
                            <SelectBox
                                options={[
                                    { label: "선불", value: "선불" },
                                    { label: "착불", value: "착불" },
                                    { label: "신용", value: "신용" },
                                    { label: "송금", value: "송금" },
                                    { label: "수금", value: "수금" },
                                ]}
                                value={searchForm.payment_type}
                                onChange={val => setSearchForm(prev => ({ ...prev, payment_type: val }))}
                                className="bg-white w-full"
                            />
                        </div>
                    </div>
                    <hr />
                    <div className="w-full grid grid-cols-3 gap-10">
                        <div className="flex items-center">
                            <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">도착지동명</p>
                            <Input
                                value={searchForm.destination_dong_name}
                                onChange={e => setSearchForm(prev => ({ ...prev, destination_dong_name: e.target.value }))}
                                className="w-full"
                            />
                        </div>
                        <div className="flex items-center">
                            <p className="min-w-[160px] text-[#1b1c1e] font-semibold text-[18px]">주문번호</p>
                            <Input
                                value={searchForm.serial_number}
                                onChange={e => setSearchForm(prev => ({ ...prev, serial_number: e.target.value }))}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button size="L" onClick={resetForm}>
                        검색 초기화
                    </Button>
                    <Button size="L" className="bg-[#132E47] border-none text-white" onClick={onClickSearch}>
                        검색
                    </Button>
                </div>
            </div>
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
